import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { easeCubicOut } from "d3-ease";
import {
  filter,
  find,
  map,
  orderBy,
  reject,
  size,
  sumBy,
  take,
  takeRight,
} from "lodash";
import { animated, useTrail } from "react-spring";
import {
  Popover,
  PopoverArrow,
  PopoverDisclosure,
  usePopoverState,
} from "reakit/Popover";
import { Portal } from "reakit/Portal";
import styled from "styled-components";
import LegendItem from "@charty/legend/item";
import { Item } from "@charty/legend/item/model";
import {
  GlossyThemeProvider,
  Radius,
  Transition,
  ZIndexes,
  background,
  content,
  prettyScrollbars,
} from "@glossy";
import { Elevation } from "@glossy/elevations";
import { Locale } from "@linkfluence/models/locale";
import { useSize } from "@lkf/hooks";
import { useIsMounted } from "@lkf/hooks";
import { division, percent } from "@tootools/number";
import Button from "@ui-kit/button";

export interface IProps {
  items: Item[];
  displayValue?: "percentage" | "absolute";
  locale: Locale;
  direction?: "row" | "column";
  align?: "start" | "center" | "left";
  setHovered?: (id: string) => void;
  className?: string;
  total?: number;
  tooltip?: (serie: Item) => ReactNode;
  onClickSerie?: (serie: Item["id"]) => void;
  disableAnimation?: boolean;
  displayVolumes?: boolean;
  withOrder?: boolean;
  magicWandCursor?: boolean;
}

const Root = styled.div<{
  isVisible: boolean;
  direction: IProps["direction"];
  align: IProps["align"];
}>`
  height: ${(props) => (props.direction === "row" ? "24px" : "100%")};
  text-align: ${(props) =>
    props.direction === "row" && props.align === "center" && "center"};
  transition: opacity ${Transition.Color};
  opacity: ${(props) => !props.isVisible && 0};
  visibility: ${(props) => !props.isVisible && "hidden"};
  color: ${content};
  position: relative;
`;

const Centerer = ({ children, active }) =>
  active ? (
    <div
      style={{
        position: "relative",
        top: "50%",
        transform: "translateY(-50%)",
      }}
    >
      {children}
    </div>
  ) : (
    children
  );

const StyledPopoverButton = styled(Button)<{
  direction: IProps["direction"];
}>`
  margin-top: ${(props) => props.direction === "column" && "6px"};
`;

const StyledPopover = styled(Popover)`
  z-index: ${ZIndexes.Tooltips};
  svg {
    fill: ${background};
    vertical-align: top;
  }
`;

const PopoverContent = styled.div<{ direction: string }>`
  background-color: ${background};
  padding: 16px 0;
  border-radius: ${Radius.Medium};
  box-shadow: ${Elevation.Elevation_3};
  max-width: ${(props) => props.direction === "row" && "280px"};
  transform: ${(props) => props.direction === "column" && "translateX(-10px)"};
  text-align: left;
`;

const PopoverScroll = styled.div`
  padding: 0 10px;
  max-height: 254px;
  ${prettyScrollbars()};
`;

const AnimatedLegendItem = animated(LegendItem);

const Legend = ({
  items,
  displayValue,
  locale,
  direction = "row",
  align = "start",
  setHovered,
  className,
  tooltip,
  total,
  onClickSerie,
  disableAnimation = false,
  withOrder = false,
  displayVolumes,
  magicWandCursor,
}: IProps) => {
  const root = useRef(null);
  const { width } = useSize(root);
  const isMounted = useIsMounted();
  const [visibleItemCount, setVisibleItemCount] = useState(null);
  const [isVisible, setVisible] = useState(false);
  const popover = usePopoverState({
    placement: direction === "row" ? "top" : "bottom-start",
  });
  // Remove null value items and compute percentage if needed
  const normalizedItems = useMemo(() => {
    const filteredItems = filter(items, (item) => item.count !== null);
    setVisibleItemCount(size(filteredItems));
    const sum = sumBy(filteredItems, "count");
    const otherSerie = find(filteredItems, { id: "other" });

    const sanitized = map(reject(filteredItems, ["id", "other"]), (item) => ({
      ...item,
      percentage: percent(item.count, sum),
    }));
    const ordered = withOrder
      ? orderBy(sanitized, "percentage", "desc")
      : sanitized;

    return otherSerie ? [...ordered, otherSerie] : ordered;
  }, [items, withOrder]);
  const itemMaxWidth =
    normalizedItems.length > 0 && normalizedItems.length < 4
      ? `${division(100, normalizedItems.length)}%`
      : "25%";
  const [trail, set] = useTrail(normalizedItems.length, () => ({
    config: { duration: disableAnimation ? 0 : 1000, easing: easeCubicOut },
    from: { opacity: 0 },
  }));

  const itemCount = size(normalizedItems);

  // Reset visible items count on items or dimensions change
  useEffect(() => {
    setVisibleItemCount(itemCount);
  }, [items, width, itemCount]);

  // Remove an item while there are hidden ones
  useEffect(() => {
    if (isMounted()) {
      if (root.current.scrollHeight > root.current.clientHeight + 2) {
        const targetVisibleItemCount = Math.min(visibleItemCount, 20); // Heuristic to reach a stable state faster with large lists
        setVisibleItemCount(targetVisibleItemCount - 1);
      } else {
        const timer = setTimeout(
          () => {
            setVisible(true);
            // Update trail
            set({ opacity: 1 });
          },
          disableAnimation ? 0 : 300
        );
        return () => clearTimeout(timer);
      }
    }
  }, [isMounted, visibleItemCount, set, disableAnimation]);

  const Item = ({ item, options }: { item: Item; options }) => (
    <AnimatedLegendItem
      item={item}
      locale={locale}
      direction={direction}
      setHovered={setHovered}
      tooltip={tooltip}
      total={total}
      opacity={options.opacity}
      itemMaxWidth={itemMaxWidth}
      onClick={onClickSerie}
      displayVolumes={displayVolumes}
      magicWandCursor={magicWandCursor}
      {...options}
    />
  );

  return (
    <Root
      ref={root}
      isVisible={isVisible}
      direction={direction}
      align={align}
      className={className}
    >
      <Centerer active={direction === "column" && align === "center"}>
        {isVisible
          ? map(take(trail, visibleItemCount), (animation, idx) => {
              const item = normalizedItems[idx];
              return (
                <Item
                  item={item}
                  key={item.id}
                  options={{
                    displayValue,
                    opacity: animation.opacity,
                  }}
                />
              );
            })
          : map(take(normalizedItems, visibleItemCount), (item, key) => (
              <Item
                item={item}
                key={key}
                options={{
                  displayValue,
                }}
              />
            ))}
        <GlossyThemeProvider name="surface">
          {visibleItemCount < itemCount && (
            <>
              <PopoverDisclosure
                {...popover}
                direction={direction}
                as={StyledPopoverButton}
                variant="primary"
                text={`+${itemCount - visibleItemCount}`}
                size="xs"
              />
              <Portal>
                <StyledPopover {...popover} aria-label="popover" tabIndex={0}>
                  <PopoverContent direction={direction}>
                    <PopoverScroll>
                      {map(
                        takeRight(
                          normalizedItems,
                          itemCount - visibleItemCount
                        ),
                        (item, key) => (
                          <LegendItem
                            item={item}
                            key={key}
                            displayValue={displayValue}
                            displayVolumes={displayVolumes}
                            direction="column"
                            locale={locale}
                            itemMaxWidth={itemMaxWidth}
                            tooltip={tooltip}
                            onClick={onClickSerie}
                            setHovered={setHovered}
                            magicWandCursor={magicWandCursor}
                          />
                        )
                      )}
                    </PopoverScroll>
                  </PopoverContent>
                  <PopoverArrow {...popover} />
                </StyledPopover>
              </Portal>
            </>
          )}
        </GlossyThemeProvider>
      </Centerer>
    </Root>
  );
};

export default Legend;
